import { QUIZ } from '../data/quiz.generated';
import { COMPUTED_BRANCHES } from '../data/computedBranches';
import type { QuizState } from '../store/quizStore';

export type RouteResult =
  | { kind: 'question'; questionId: string }
  | { kind: 'complete' };

/**
 * Resolve the next destination given the current question, the chosen answer
 * (when applicable), and the current state.
 *
 * - For `_transition` and `_freeText`, `answerOptionId` is ignored; we use
 *   the lone outgoing edge from `options[0]`.
 * - For everything else, the chosen `answerOptionId` is required and we look
 *   up its `nextQuestionId`.
 * - `COMPUTED_BRANCHES[questionId]` overrides the CSV routing entirely (used
 *   for branches that depend on state, not on which answer was just picked).
 */
export function getNextQuestionId(
  currentQuestionId: string,
  answerOptionId: string | null,
  state: QuizState,
): RouteResult {
  // 1. Computed branch override
  const computed = COMPUTED_BRANCHES[currentQuestionId];
  if (computed) {
    const next = computed(state);
    return next == null
      ? { kind: 'complete' }
      : { kind: 'question', questionId: next };
  }

  const q = QUIZ.questions[currentQuestionId];
  if (!q) throw new Error(`Unknown question ${currentQuestionId}`);

  // 2. Single-edge types: transition + freeText
  if (q.type === '_transition' || q.type === '_freeText') {
    const next = q.options[0]?.nextQuestionId ?? null;
    return next == null
      ? { kind: 'complete' }
      : { kind: 'question', questionId: next };
  }

  // 3. Per-answer routing
  if (!answerOptionId) {
    throw new Error(`answerOptionId required for ${currentQuestionId} (${q.type})`);
  }
  const opt = q.options.find((o) => o.id === answerOptionId);
  if (!opt) {
    throw new Error(`Unknown answer ${answerOptionId} on question ${currentQuestionId}`);
  }
  return opt.nextQuestionId == null
    ? { kind: 'complete' }
    : { kind: 'question', questionId: opt.nextQuestionId };
}

/**
 * Determines where to send a user when they hit `/`. If they have an
 * in-progress session, resume; otherwise enter the quiz.
 */
export function resolveStartId(currentQuestionId: string | null): string {
  return currentQuestionId ?? QUIZ.entryQuestionId;
}

